import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Drawer, Spin, Badge, Tooltip, ConfigProvider } from 'antd';
import {
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  BankOutlined,
  DropboxOutlined,
  SettingOutlined,
  LoginOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
  BarsOutlined,
  AlignCenterOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { LoginContext } from '../../context/loginContext';
import { SucursalContext } from '../../context/sucursalContext';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { CarritoContext } from '../../context/carritoContext';
import NotificacionesBadge from '../../components/NotificacionesBadge';

const { Header, Content } = Layout;

const MainLayout = ({ children }) => {
  const { user, login, logout: logoutContext } = useContext(LoginContext);
  const { logout: logoutAuth0, isAuthenticated, isLoading, user: auth0User } = useAuth0();
  const carritoContext = useContext(CarritoContext);
  const cantidadArticulosUnicos = carritoContext?.cantidadArticulosUnicos || 0;

  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { limpiarSucursal } = useContext(SucursalContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const autenticarCliente = async () => {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/login-cliente`, {
          usuario: auth0User.email || auth0User.sub,
          telefono: auth0User.phone_number || null,
          sucursal: 1,
          nombre: auth0User.name
        });

        const { usuario: usuarioBackend, token } = response.data;
        login({ ...usuarioBackend, token });
        console.log('LOGIN SETEADO:', { ...usuarioBackend, token }); // <--- agregalo
      } catch (error) {
        console.error('Error al autenticar cliente en MainLayout:', error);
      }
    };

    const justLoggedOut = sessionStorage.getItem('justLoggedOut');

    if (isAuthenticated && auth0User && !user && !justLoggedOut) {
      autenticarCliente();
    }

    if (justLoggedOut) {
      sessionStorage.removeItem('justLoggedOut');
      localStorage.removeItem('user'); // 🔧 Asegura que nada quedó en cache
    }

  }, [isAuthenticated, auth0User, user, login]);

  const handleLogout = () => {
    logoutContext(); // limpia el contexto y localStorage
    setMobileOpen(false);
    sessionStorage.setItem('justLoggedOut', 'true');

    if (isAuthenticated && user?.tiu_descripcion === 'Cliente') {
      setTimeout(() => {
        logoutAuth0({
          logoutParams: {
            returnTo: process.env.REACT_APP_AUTH0_LOGOUT_RETURN_TO || window.location.origin
          }
        });
      }, 100); // 🔁 Esperar un tick para limpiar antes de redirigir
    } else {
      navigate('/');
    }
  };

  const getMenuItems = () => {
    const base = [
      {
        key: '/home',
        icon: <HomeOutlined />,
        label: <Link to="/home">Inicio</Link>
      }
    ];

    if (user?.tiu_descripcion === 'Empresa') {
      base.push(
        { key: '/lista-sucursal-empresa', icon: <BankOutlined />, label: <Link to="/lista-sucursal-empresa">Sucursales</Link> },
        { key: '/lista-articulo-empresa', icon: <DropboxOutlined />, label: <Link to="/lista-articulo-empresa">Artículos</Link> },
        { key: '/lista-usuarios-empresa', icon: <UserOutlined />, label: <Link to="/lista-usuarios-empresa">Usuarios</Link> },
        { key: '/configuracion-empresa', icon: <SettingOutlined />, label: <Link to="/configuracion-empresa">Configuración</Link> },
        { key: '/categorias-empresa', icon: <AlignCenterOutlined />, label: <Link to="/categorias-empresa">Categorias</Link> }
      );
    } else if (user?.tiu_descripcion === 'Sucursal') {
      base.push(
        { key: '/lista-articulos-empresa-sucursal', icon: <DropboxOutlined />, label: <Link to="/lista-articulos-empresa-sucursal">Art. Empresa</Link> },
        { key: '/articulos-habilitados-sucursal', icon: <AlignCenterOutlined />, label: <Link to="/articulos-habilitados-sucursal">Habilitados</Link> },
        { key: '/pedidos-sucursal', icon: <BarsOutlined />, label: <Link to="/pedidos-sucursal">Pedidos</Link> },
        { key: '/carrusel-sucursal', icon: <ShopOutlined />, label: <Link to="/carrusel-sucursal">Carrusel</Link> }
      );
    } else if (user?.tiu_descripcion === 'Cliente') {
      base.push(
        { key: '/carrito-cliente', icon: <DropboxOutlined />, label: <Link to="/carrito-cliente">Carrito</Link> },
        { key: '/datos-cliente', icon: <UserOutlined />, label: <Link to="/datos-cliente">Mi perfil</Link> },
        { key: '/pedidos-cliente', icon: <BarsOutlined />, label: <Link to="/pedidos-cliente">Pedidos</Link> }
      );
    }

    return base;
  };

  const menuItems = getMenuItems();
  const isCliente = user?.tiu_descripcion === 'Cliente';

  if (isLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const contenidoLayout = (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#e10a25',
        },
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <style>
        {`
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu-selected {
            background-color: #e10a25 !important;
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected::after,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu-selected::after {
            border-bottom-color: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected::before,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu-selected::before {
            background-color: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item:hover,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu:hover {
            background-color: rgba(225, 10, 37, 0.3) !important;
          }
          .ant-menu-dark .ant-menu-item-selected > a,
          .ant-menu-dark .ant-menu-item-selected > span,
          .ant-menu-dark .ant-menu-item-selected > .ant-menu-item-icon,
          .ant-menu-dark .ant-menu-item-selected > .ant-menu-title-content {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .ant-menu-item-icon,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu-selected .ant-menu-item-icon {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal .ant-menu-item-selected,
          .ant-menu-dark.ant-menu-horizontal .ant-menu-submenu-selected {
            background: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected * {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .anticon,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-submenu-selected .anticon {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected a,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected a * {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .ant-menu-item-icon,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .ant-menu-title-content {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected {
            background: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected > * {
            color: #fff !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected *,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected span,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected div,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected a,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .ant-menu-title-content,
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected .ant-menu-item-icon {
            background-color: transparent !important;
            background: transparent !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected {
            background-color: #e10a25 !important;
            background: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected[style*="background"] {
            background-color: #e10a25 !important;
            background: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected > span[style*="background"],
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected > div[style*="background"],
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected > a[style*="background"] {
            background-color: transparent !important;
            background: transparent !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected [style*="52c41a"],
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected [style*="rgb(82, 196, 26)"],
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected [style*="green"] {
            background-color: transparent !important;
            background: transparent !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected {
            background: #e10a25 !important;
            background-color: #e10a25 !important;
          }
          .ant-menu-dark.ant-menu-horizontal > .ant-menu-item-selected * {
            background: transparent !important;
            background-color: transparent !important;
          }
        `}
      </style>
      <Header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 16px',
          background: '#000000',
          height: '48px',
          lineHeight: '48px',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {isMobile ? (
            <Button type="text" icon={<MenuOutlined />} onClick={() => setMobileOpen(true)} style={{ color: '#fff', fontSize: '20px' }} />
          ) : (
            <>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <Menu
                  theme="dark"
                  mode="horizontal"
                  selectedKeys={[location.pathname]}
                  items={menuItems.map(item => ({ 
                    ...item, 
                    key: item.key || (item.label?.props?.to || item.key)
                  }))}
                  style={{ minWidth: 0, backgroundColor: '#000000' }}
                />
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isCliente && (
            <>
              <Link
                to="/carrito-cliente"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  color: 'white'
                }}
              >
                <Badge count={cantidadArticulosUnicos} size="small" offset={[6, -2]}>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: 'white' }} />
                </Badge>
              </Link>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 8
              }}>
                <NotificacionesBadge />
              </div>
            </>
          )}

          {user ? (
            <Dropdown
              menu={{
                items: [
                  ...(isCliente
                    ? [
                      {
                        key: 'changeSucursal',
                        icon: <BankOutlined />,
                        label: (
                          <span onClick={() => {
                            localStorage.removeItem('sucursalSeleccionada');
                            limpiarSucursal();
                            navigate('/');
                          }}>
                            Cambiar sucursal
                          </span>
                        )
                      }
                    ]
                    : []),
                  {
                    key: 'logout',
                    icon: <LogoutOutlined style={{ color: 'red' }} />,
                    label: <span style={{ color: 'red' }} onClick={handleLogout}>Cerrar sesión</span>
                  }
                ]
              }}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={<UserOutlined />}
                style={{
                  color: '#fff',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Dropdown>
          ) : (
            <>
              <Tooltip title="Cambiar de sucursal">
                <Button
                  type="text"
                  icon={<BankOutlined />}
                  onClick={() => {
                    localStorage.removeItem('sucursalSeleccionada');
                    limpiarSucursal();
                    navigate('/');
                  }}
                  style={{
                    color: 'white',
                    fontSize: 18,
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8
                  }}
                />
              </Tooltip>
              <Tooltip title="Ingresar como empresa">
                <Button
                  type="text"
                  icon={<ShopOutlined />}
                  onClick={() => navigate('/login-empresa')}
                  style={{
                    color: 'white',
                    fontSize: 18,
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8
                  }}
                />
              </Tooltip>
              <Button
                type="text"
                onClick={() => navigate('/login')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'white',
                  fontWeight: 500,
                  padding: '0 12px',
                  height: 36,
                  borderRadius: 8,
                }}
                icon={<LoginOutlined style={{ color: '#e10a25', fontSize: 16 }} />}
              >
              </Button>
            </>
          )}
        </div>
      </Header>

      <Drawer
        title="Menú"
        placement="left"
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={[
            ...menuItems,
            {
              key: 'logout',
              icon: <LogoutOutlined style={{ color: 'red' }} />,
              label: 'Cerrar sesión',
              style: { color: 'red' }
            }
          ]}
          onClick={({ key }) => {
            if (key === 'logout') handleLogout();
            setMobileOpen(false);
          }}
        />
      </Drawer>

      <Content style={{ padding: '20px', background: '#fff' }}>{children}</Content>
    </Layout>
    </ConfigProvider>
  );

  return contenidoLayout;
};

export default MainLayout;